import { useState, useEffect, useCallback, useContext, createContext } from 'react';
import { getPref, setPref } from '../services/preferences';

// ─── Shape ──────────────────────────────────────────────────────────────────
export interface AppContext {
  companyId: number | null;
  branchId: number | null;
  branchCode?: string | null;
  financialYearId: number | null;
  branchName: string | null;
  financialYearName: string | null;
  fyStartDate: string | null;
  fyEndDate: string | null;
}

const EVENT_NAME = 'app-context-changed';
/** The one server preference that holds where a person last worked: GLOBAL, so it follows them into any company. */
const CONTEXT_PREF = 'context';

/**
 * The application context lives in MEMORY for the page and on the SERVER between pages.
 *
 * Company, work location and financial year used to be read out of localStorage on every API
 * request — three keys, reconciled on every read. Nothing about them is in the browser now. The
 * current values are a module variable, read synchronously by the request interceptor and by every
 * screen; every change is also written to one global preference on the server, and that preference
 * is what restores the context after a reload or on another machine. A browser wiped clean forgets
 * nothing, and two browsers agree.
 */
let current: AppContext = blank();
/** The signed-in user's own company, seeded by the auth service — the fallback when no context was chosen. */
let identityCompanyId: number | null = null;
/**
 * True while the company in memory is only the identity fallback rather than something the user
 * picked. The distinction matters exactly once, at boot: the session is rehydrated (which seeds the
 * fallback) BEFORE the saved preference is read, so without this the fallback counted as "something
 * memory already knows" and outranked the company the user had actually chosen. Selecting SV KNITS
 * and refreshing put you back on ElectEmb every time.
 */
let companyIsIdentityFallback = false;

function blank(): AppContext {
  return {
    companyId: null, branchId: null, branchCode: null, financialYearId: null,
    branchName: null, financialYearName: null, fyStartDate: null, fyEndDate: null,
  };
}

/** Called by the auth service whenever a session is established, so a context with no company chosen falls back to the user's own. */
export function seedContextIdentity(companyId: number | null | undefined): void {
  identityCompanyId = companyId ?? null;
  if (current.companyId == null && identityCompanyId != null) {
    current = { ...current, companyId: identityCompanyId };
    companyIsIdentityFallback = true;
    window.dispatchEvent(new Event(EVENT_NAME));
  }
}

/**
 * Bring back the context the server holds for this person. Run once the preference bundle has
 * loaded — at boot, and after login. Fills only what memory does not already know, so a choice made
 * on this page is never undone by an older one from the server.
 */
export function restoreContextFromPreferences(): void {
  const saved = getPref<Partial<AppContext> | null>(CONTEXT_PREF, null, 'global');
  foldInLegacyBrowserContext();
  if (saved) {
    // What memory already holds still wins — a choice made on this page must not be undone by an
    // older one from the server. The company is the exception when it is only the identity
    // fallback: that is not a choice, and letting it win is what reset the selection on every
    // refresh. A company the user actually picked is still kept.
    const chosen = Object.entries(current)
      .filter(([k, v]) => v != null && !(k === 'companyId' && companyIsIdentityFallback));
    current = {
      ...blank(),
      ...saved,
      ...Object.fromEntries(chosen),
    } as AppContext;
    if (saved.companyId != null && companyIsIdentityFallback) companyIsIdentityFallback = false;
  }
  if (current.companyId == null && identityCompanyId != null) {
    current.companyId = identityCompanyId;
    companyIsIdentityFallback = true;
  }
  window.dispatchEvent(new Event(EVENT_NAME));
}

/**
 * The last thing this module ever reads from the browser: a context left behind by the version
 * that kept one there. Folded into memory once so nobody loses the branch they were on when the
 * upgrade landed, then deleted. Only ever fills a blank — a context already known is not overwritten.
 */
function foldInLegacyBrowserContext(): void {
  try {
    const raw = localStorage.getItem('crm_app_context');
    if (raw) {
      const legacy = JSON.parse(raw) as Partial<AppContext>;
      const known = current as unknown as Record<string, unknown>;
      const fill = Object.fromEntries(Object.entries(legacy).filter(([k, v]) => v != null && known[k] == null));
      current = { ...current, ...fill };
    }
    ['crm_app_context', 'crm_selected_unit', 'user', 'authToken', 'authExpiresAt',
     'platformExpiresAt', 'platformUser', 'platformToken'].forEach(k => localStorage.removeItem(k));
  } catch { /* blocked storage — nothing to fold in and nothing to remove */ }
}

/** Synchronous read — for use outside React (e.g. API interceptor). */
export function getAppContext(): AppContext {
  return current;
}

/** Forget everything — logout. */
export function clearAppContext(): void {
  current = blank();
  identityCompanyId = null;
  companyIsIdentityFallback = false;
  window.dispatchEvent(new Event(EVENT_NAME));
}

// ─── Transient "Full Report" toggle (Compliance reports) ────────────────────
// In-memory only (deliberately NOT persisted): when true, Compliance report
// downloads include fully-absent employees too. The API request interceptor
// forwards it as the X-Full-Report header; the backend only reads it while
// generating reports, so it is harmless on other requests. Resets to off on
// page reload.
let _fullReport = false;
export function setFullReport(on: boolean): void { _fullReport = on; }
export function getFullReport(): boolean { return _fullReport; }

/** Write a partial update, persist it for this person, and notify listeners. */
export function setAppContext(partial: Partial<AppContext>): void {
  current = { ...current, ...partial };
  // Anything written through here is deliberate, so the company is no longer a fallback.
  if (partial.companyId != null) companyIsIdentityFallback = false;
  setPref(CONTEXT_PREF, current, 'global');
  window.dispatchEvent(new Event(EVENT_NAME));
}

// ─── React Context ──────────────────────────────────────────────────────────
const Ctx = createContext<AppContext>(blank());

export function useAppContext(): AppContext {
  const ctx = useContext(Ctx);
  return ctx;
}

/** React hook — re-renders when app context changes. */
export function useAppContextState(): AppContext {
  const [ctx, setCtx] = useState<AppContext>(() => current);

  const refresh = useCallback(() => setCtx({ ...current }), []);

  useEffect(() => {
    window.addEventListener(EVENT_NAME, refresh);
    // Also listen to legacy events for backward compat
    window.addEventListener('unit-changed', refresh);
    window.addEventListener('financial-year-updated', refresh);
    window.addEventListener('company-updated', refresh);
    return () => {
      window.removeEventListener(EVENT_NAME, refresh);
      window.removeEventListener('unit-changed', refresh);
      window.removeEventListener('financial-year-updated', refresh);
      window.removeEventListener('company-updated', refresh);
    };
  }, [refresh]);

  return ctx;
}

export { Ctx as AppContextReact };
